/**
 * Custom landing page configurations for specific tenants
 * Add custom content per subdomain here
 */

export interface TenantLandingConfig {
  subdomain: string;
  landingStyle?: 'default' | 'flexPro' | 'harkPremium';
  tenantLogoUrl?: string;
  seo?: {
    title?: string;
    description?: string;
    ogImage?: string;
  };
  hero: {
    title: string;
    subtitle: string;
    description: string;
  };
  heroBilingual?: {
    titleAl: string;
    titleEn: string;
    subtitleAl: string;
    subtitleEn: string;
    descriptionAl: string;
    descriptionEn: string;
  };
  features: Array<{
    title: string;
    description: string;
    icon?: string;
  }>;
  benefits: Array<{
    title: string;
    description: string;
  }>;
  cta: {
    primary: string;
    secondary: string;
  };
  ctaBilingual?: {
    primaryAl: string;
    primaryEn: string;
    secondaryAl: string;
    secondaryEn: string;
  };
  locations?: Array<{
    name: string;
    city: string;
    focusAl: string;
    focusEn: string;
    imageUrl?: string;
    instagramUrl: string;
    instagramHandle: string;
  }>;
  pricingHighlights?: Array<{
    locationKey: 'gymTirane' | 'sportClub' | 'greenCoast';
    durationKey: 'daily' | 'monthly' | 'twoMonths' | 'threeMonths' | 'sixMonths' | 'annual' | 'fixed';
    labelAl: string;
    labelEn: string;
    price: number;
    currency: string;
    periodAl: string;
    periodEn: string;
    noteAl?: string;
    noteEn?: string;
  }>;
  socialLinks?: Array<{
    name: string;
    url: string;
    handle: string;
    descriptionAl: string;
    descriptionEn: string;
  }>;
  flexKitchen?: {
    name: string;
    url: string;
    handle: string;
    imageUrl?: string;
    descriptionAl: string;
    descriptionEn: string;
  };
}

// Custom configurations for specific tenants
export const TENANT_CONFIGS: Record<string, TenantLandingConfig> = {
  repeat: {
    subdomain: 'repeat',
    seo: {
      title: 'Repeat Fitness - Where Champions Train',
      description: 'Experience elite-level training with state-of-the-art facilities and expert coaches at Repeat Fitness.',
    },
    hero: {
      title: 'Welcome to Repeat Fitness',
      subtitle: 'Where Champions Train',
      description:
        'Experience elite-level training with state-of-the-art facilities and expert coaches. Join the winning team at Repeat Fitness.',
    },
    features: [
      {
        title: 'Elite Training Programs',
        description:
          'Custom-designed programs by our world-class coaches to help you reach peak performance.',
      },
      {
        title: 'Olympic-Grade Equipment',
        description:
          'Train with the same equipment used by professional athletes around the world.',
      },
      {
        title: 'Sports Performance Lab',
        description:
          'Advanced body composition analysis, VO2 max testing, and performance tracking.',
      },
      {
        title: 'Recovery Center',
        description:
          'Sauna, ice baths, massage therapy, and stretching areas for optimal recovery.',
      },
      {
        title: 'Nutritionist On-Site',
        description:
          'Work with certified nutritionists to fuel your body for maximum results.',
      },
      {
        title: 'Group Training Sessions',
        description:
          'High-energy group classes led by motivating coaches to push you beyond limits.',
      },
    ],
    benefits: [
      {
        title: 'Professional Athletes Welcome',
        description:
          'Train alongside professional athletes and be part of a winning culture.',
      },
      {
        title: '24/7 Access',
        description:
          'Train on your schedule with round-the-clock facility access.',
      },
      {
        title: 'Competition Preparation',
        description:
          'Specialized programs for athletes preparing for competitions and events.',
      },
      {
        title: 'Video Analysis',
        description:
          'Advanced movement analysis to perfect your form and technique.',
      },
      {
        title: 'Private Training Rooms',
        description:
          'Exclusive spaces for focused training sessions with your coach.',
      },
      {
        title: 'Champions Community',
        description:
          'Network with like-minded high performers and build lasting connections.',
      },
    ],
    cta: {
      primary: 'Start Your Trial',
      secondary: 'Book a Tour',
    },
  },

  flex: {
    subdomain: 'flex',
    landingStyle: 'flexPro',
    tenantLogoUrl: '/gym1.png',
    seo: {
      title: 'Flex Gym - Premium Fitness & Wellbeing',
      description: 'Modern training, dynamic classes, and premium fitness spaces in Tirana and Palase. 3 locations, 1 professional standard.',
      ogImage: '/locations/flex-gym-tirane.jpg',
    },
    hero: {
      title: 'Welcome to Flex Fitness Studio',
      subtitle: 'Strength Meets Flexibility',
      description:
        'Transform your body and mind with our unique blend of strength training, yoga, and mobility work. Find your balance at Flex.',
    },
    heroBilingual: {
      titleAl: 'Flex - Fitness dhe Wellbeing',
      titleEn: 'Flex - Fitness and Wellbeing',
      subtitleAl: '3 lokacione, 1 standard profesional',
      subtitleEn: '3 locations, 1 professional standard',
      descriptionAl:
        'Trajnim modern, klasa dinamike dhe ambiente premium ne Tirane dhe Palase. Zgjidh planin qe pershtatet me ritmin tend.',
      descriptionEn:
        'Modern training, dynamic classes, and premium spaces in Tirana and Palase. Choose the plan that matches your pace.',
    },
    features: [
      {
        title: 'Yoga & Pilates',
        description:
          'Daily classes ranging from gentle flow to power yoga, plus reformer pilates sessions.',
      },
      {
        title: 'Functional Training',
        description:
          'Build real-world strength with functional movement patterns and bodyweight training.',
      },
      {
        title: 'Mobility & Flexibility',
        description:
          'Dedicated stretching and mobility sessions to improve range of motion and prevent injury.',
      },
      {
        title: 'Mind-Body Classes',
        description:
          'Meditation, breathwork, and mindfulness sessions to complement your physical practice.',
      },
      {
        title: 'Small Group Sessions',
        description:
          'Intimate class sizes (max 12 people) ensure personalized attention from instructors.',
      },
      {
        title: 'Wellness Workshops',
        description:
          'Monthly workshops on nutrition, stress management, sleep optimization, and more.',
      },
    ],
    benefits: [
      {
        title: 'Holistic Approach',
        description:
          'We focus on complete wellness - physical, mental, and emotional health.',
      },
      {
        title: 'Beginner-Friendly',
        description:
          'Welcoming environment where all fitness levels are supported and encouraged.',
      },
      {
        title: 'Injury Prevention',
        description:
          'Our programs emphasize proper movement patterns to keep you training safely.',
      },
      {
        title: 'Stress Relief',
        description:
          'Escape the daily grind with our calming studio environment and restorative classes.',
      },
      {
        title: 'Expert Instructors',
        description:
          'All instructors are certified in multiple disciplines including yoga, pilates, and strength.',
      },
      {
        title: 'Inclusive Community',
        description:
          'Join a supportive community that celebrates progress over perfection.',
      },
    ],
    cta: {
      primary: 'Try Your First Class',
      secondary: 'View Schedule',
    },
    ctaBilingual: {
      primaryAl: 'Regjistrohu tani',
      primaryEn: 'Join now',
      secondaryAl: 'Hyr si anetar',
      secondaryEn: 'Member login',
    },
    locations: [
      {
        name: 'Flex Gym Tirane',
        city: 'Tirane',
        focusAl: 'Palester moderne dhe strefit i perditshme',
        focusEn: 'Modern gym and daily strength training',
        imageUrl: '/locations/flex-gym-tirane.jpg',
        instagramUrl: 'https://www.instagram.com/flexgym_tirane/',
        instagramHandle: '@flexgym_tirane',
      },
      {
        name: 'Flex Classes & Sport Club',
        city: 'Tirane',
        focusAl: 'CrossFit, Spinning, Pilates dhe klasa grupi',
        focusEn: 'CrossFit, Spinning, Pilates, and group classes',
        imageUrl: '/locations/flex-sport-club-tirane.jpg',
        instagramUrl: 'https://www.instagram.com/flexsportsclub.al/',
        instagramHandle: '@flexsportsclub.al',
      },
      {
        name: 'Flex Green Coast',
        city: 'Palase',
        focusAl: 'Fitness premium ne bregdet',
        focusEn: 'Premium coastal fitness experience',
        imageUrl: '/locations/flex-green-coast-palase.jpg',
        instagramUrl: 'https://www.instagram.com/flexgymgreencoast.al/',
        instagramHandle: '@flexgymgreencoast.al',
      },
    ],
    pricingHighlights: [
      {
        locationKey: 'gymTirane',
        durationKey: 'daily',
        labelAl: 'Flex Gym Tirane - Ditore',
        labelEn: 'Flex Gym Tirana - Daily',
        price: 1000,
        currency: 'ALL',
        periodAl: '1 dite',
        periodEn: '1 day',
      },
      {
        locationKey: 'gymTirane',
        durationKey: 'monthly',
        labelAl: 'Flex Gym Tirane - Mujore (3 here ne jave)',
        labelEn: 'Flex Gym Tirana - Monthly (3 times per week)',
        price: 6500,
        currency: 'ALL',
        periodAl: '3 here ne jave',
        periodEn: '3 times per week',
      },
      {
        locationKey: 'gymTirane',
        durationKey: 'monthly',
        labelAl: 'Flex Gym Tirane - Mujore (4 here ne jave)',
        labelEn: 'Flex Gym Tirana - Monthly (4 times per week)',
        price: 7000,
        currency: 'ALL',
        periodAl: '4 here ne jave',
        periodEn: '4 times per week',
      },
      {
        locationKey: 'gymTirane',
        durationKey: 'monthly',
        labelAl: 'Flex Gym Tirane - Mujore',
        labelEn: 'Flex Gym Tirana - Monthly',
        price: 7500,
        currency: 'ALL',
        periodAl: '7 here ne jave',
        periodEn: '7 times per week',
      },
      {
        locationKey: 'gymTirane',
        durationKey: 'threeMonths',
        labelAl: 'Flex Gym Tirane - 3 Mujore (3 here ne jave)',
        labelEn: 'Flex Gym Tirana - 3 Months (3 times per week)',
        price: 14000,
        currency: 'ALL',
        periodAl: '3 here ne jave',
        periodEn: '3 times per week',
      },
      {
        locationKey: 'gymTirane',
        durationKey: 'threeMonths',
        labelAl: 'Flex Gym Tirane - 3 Mujore (4 here ne jave)',
        labelEn: 'Flex Gym Tirana - 3 Months (4 times per week)',
        price: 15500,
        currency: 'ALL',
        periodAl: '4 here ne jave',
        periodEn: '4 times per week',
      },
      {
        locationKey: 'gymTirane',
        durationKey: 'threeMonths',
        labelAl: 'Flex Gym Tirane - 3 Mujore',
        labelEn: 'Flex Gym Tirana - 3 Months',
        price: 17000,
        currency: 'ALL',
        periodAl: '7 here ne jave',
        periodEn: '7 times per week',
      },
      {
        locationKey: 'gymTirane',
        durationKey: 'sixMonths',
        labelAl: 'Flex Gym Tirane - 6 Mujore',
        labelEn: 'Flex Gym Tirana - 6 Months',
        price: 30000,
        currency: 'ALL',
        periodAl: '7 here ne jave',
        periodEn: '7 times per week',
      },
      {
        locationKey: 'gymTirane',
        durationKey: 'annual',
        labelAl: 'Flex Gym Tirane - 1 Vjecare (3 here ne jave)',
        labelEn: 'Flex Gym Tirana - 1 Year (3 times per week)',
        price: 36000,
        currency: 'ALL',
        periodAl: '3 here ne jave',
        periodEn: '3 times per week',
      },
      {
        locationKey: 'gymTirane',
        durationKey: 'annual',
        labelAl: 'Flex Gym Tirane - 1 Vjecare (4 here ne jave)',
        labelEn: 'Flex Gym Tirana - 1 Year (4 times per week)',
        price: 39000,
        currency: 'ALL',
        periodAl: '4 here ne jave',
        periodEn: '4 times per week',
      },
      {
        locationKey: 'gymTirane',
        durationKey: 'annual',
        labelAl: 'Flex Gym Tirane - 1 Vjecare',
        labelEn: 'Flex Gym Tirana - 1 Year',
        price: 42000,
        currency: 'ALL',
        periodAl: 'akses i plote',
        periodEn: 'full access',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'monthly',
        labelAl: 'Sport Club - CrossFit Mujore',
        labelEn: 'Sport Club - CrossFit Monthly',
        price: 7500,
        currency: 'ALL',
        periodAl: '3x ne jave',
        periodEn: '3x per week',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'threeMonths',
        labelAl: 'Sport Club - CrossFit 3 Muaj',
        labelEn: 'Sport Club - CrossFit 3 Months',
        price: 17500,
        currency: 'ALL',
        periodAl: '4x ne jave',
        periodEn: '4x per week',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'annual',
        labelAl: 'Sport Club - CrossFit 1 Vjecare (3x jave)',
        labelEn: 'Sport Club - CrossFit 1 Year (3x week)',
        price: 36000,
        currency: 'ALL',
        periodAl: '3x ne jave',
        periodEn: '3x per week',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'annual',
        labelAl: 'Sport Club - CrossFit 1 Vjecare (4x jave)',
        labelEn: 'Sport Club - CrossFit 1 Year (4x week)',
        price: 39000,
        currency: 'ALL',
        periodAl: '4x ne jave',
        periodEn: '4x per week',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'annual',
        labelAl: 'Sport Club - CrossFit 1 Vjecare (cdo dite)',
        labelEn: 'Sport Club - CrossFit 1 Year (daily)',
        price: 42000,
        currency: 'ALL',
        periodAl: 'cdo dite',
        periodEn: 'daily',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'monthly',
        labelAl: 'Sport Club - Spinning Mujore',
        labelEn: 'Sport Club - Spinning Monthly',
        price: 7500,
        currency: 'ALL',
        periodAl: '3x ne jave',
        periodEn: '3x per week',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'threeMonths',
        labelAl: 'Sport Club - Spinning 3 Muaj',
        labelEn: 'Sport Club - Spinning 3 Months',
        price: 19000,
        currency: 'ALL',
        periodAl: 'cdo dite',
        periodEn: 'daily',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'annual',
        labelAl: 'Sport Club - Spinning 1 Vjecare',
        labelEn: 'Sport Club - Spinning 1 Year',
        price: 42000,
        currency: 'ALL',
        periodAl: 'cdo dite',
        periodEn: 'daily',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'monthly',
        labelAl: 'Sport Club - Pilates Mujore',
        labelEn: 'Sport Club - Pilates Monthly',
        price: 11500,
        currency: 'ALL',
        periodAl: '3x ne jave',
        periodEn: '3x per week',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'threeMonths',
        labelAl: 'Sport Club - Pilates 3 Muaj',
        labelEn: 'Sport Club - Pilates 3 Months',
        price: 22500,
        currency: 'ALL',
        periodAl: '3x ne jave',
        periodEn: '3x per week',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'annual',
        labelAl: 'Sport Club - Pilates 1 Vjecare',
        labelEn: 'Sport Club - Pilates 1 Year',
        price: 69000,
        currency: 'ALL',
        periodAl: '3x ne jave',
        periodEn: '3x per week',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'monthly',
        labelAl: 'Sport Club - Gym + CrossFit Mujore',
        labelEn: 'Sport Club - Gym + CrossFit Monthly',
        price: 8500,
        currency: 'ALL',
        periodAl: 'cdo dite',
        periodEn: 'daily',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'threeMonths',
        labelAl: 'Sport Club - Gym + CrossFit 3 Muaj',
        labelEn: 'Sport Club - Gym + CrossFit 3 Months',
        price: 19000,
        currency: 'ALL',
        periodAl: 'cdo dite',
        periodEn: 'daily',
      },
      {
        locationKey: 'sportClub',
        durationKey: 'annual',
        labelAl: 'Sport Club - Gym + CrossFit + Spinning 1 Vjecare',
        labelEn: 'Sport Club - Gym + CrossFit + Spinning 1 Year',
        price: 58000,
        currency: 'ALL',
        periodAl: 'cdo dite',
        periodEn: 'daily',
      },
      {
        locationKey: 'greenCoast',
        durationKey: 'daily',
        labelAl: 'Flex Green Coast - Ditore',
        labelEn: 'Flex Green Coast - Daily',
        price: 2000,
        currency: 'ALL',
        periodAl: '1 dite',
        periodEn: '1 day',
      },
      {
        locationKey: 'greenCoast',
        durationKey: 'fixed',
        labelAl: 'Flex Green Coast - 7 Seanca',
        labelEn: 'Flex Green Coast - 7 Sessions',
        price: 11900,
        currency: 'ALL',
        periodAl: '7 seanca',
        periodEn: '7 sessions',
      },
      {
        locationKey: 'greenCoast',
        durationKey: 'fixed',
        labelAl: 'Flex Green Coast - 14 Seanca',
        labelEn: 'Flex Green Coast - 14 Sessions',
        price: 19600,
        currency: 'ALL',
        periodAl: '14 seanca',
        periodEn: '14 sessions',
      },
      {
        locationKey: 'greenCoast',
        durationKey: 'fixed',
        labelAl: 'Flex Green Coast - 21 Seanca',
        labelEn: 'Flex Green Coast - 21 Sessions',
        price: 25400,
        currency: 'ALL',
        periodAl: '21 seanca',
        periodEn: '21 sessions',
      },
      {
        locationKey: 'greenCoast',
        durationKey: 'monthly',
        labelAl: 'Flex Green Coast - Mujore',
        labelEn: 'Flex Green Coast - Monthly',
        price: 9500,
        currency: 'ALL',
        periodAl: 'akses i plote',
        periodEn: 'full access',
      },
      {
        locationKey: 'greenCoast',
        durationKey: 'twoMonths',
        labelAl: 'Flex Green Coast - 2 Mujore',
        labelEn: 'Flex Green Coast - 2 Months',
        price: 15500,
        currency: 'ALL',
        periodAl: 'akses i plote',
        periodEn: 'full access',
      },
      {
        locationKey: 'greenCoast',
        durationKey: 'threeMonths',
        labelAl: 'Flex Green Coast - 3 Mujore',
        labelEn: 'Flex Green Coast - 3 Months',
        price: 19500,
        currency: 'ALL',
        periodAl: 'akses i plote',
        periodEn: 'full access',
      },
      {
        locationKey: 'greenCoast',
        durationKey: 'annual',
        labelAl: 'Flex Tirane + Green Coast - 1 Vjecare',
        labelEn: 'Flex Tirana + Green Coast - 1 Year',
        price: 52000,
        currency: 'ALL',
        periodAl: 'akses i plote',
        periodEn: 'full access',
      },
    ],
    socialLinks: [
      {
        name: 'Flex Gym Tirane',
        url: 'https://www.instagram.com/flexgym_tirane/',
        handle: '@flexgym_tirane',
        descriptionAl: 'Risi, transformime dhe energji nga palestra ne Tirane.',
        descriptionEn: 'Updates, transformations, and daily gym energy in Tirana.',
      },
      {
        name: 'Flex Classes & Sport Club',
        url: 'https://www.instagram.com/flexsportsclub.al/',
        handle: '@flexsportsclub.al',
        descriptionAl: 'Programet e klasave dhe komuniteti sportiv ne nje vend.',
        descriptionEn: 'Class programs and sport community updates in one place.',
      },
      {
        name: 'Flex Green Coast',
        url: 'https://www.instagram.com/flexgymgreencoast.al/',
        handle: '@flexgymgreencoast.al',
        descriptionAl: 'Lifestyle fitness nga Green Coast ne Palase.',
        descriptionEn: 'Coastal fitness lifestyle from Green Coast in Palase.',
      },
    ],
    flexKitchen: {
      name: 'Flex Kitchen',
      url: 'https://www.instagram.com/flexkitchen.al/',
      handle: '@flexkitchen.al',
      imageUrl: '/locations/flex-kitchen.jpg',
      descriptionAl: 'Ushqim i balancuar per performance dhe stil jete aktiv.',
      descriptionEn: 'Balanced meals built for performance and an active lifestyle.',
    },
  },

  hark: {
    subdomain: 'hark',
    landingStyle: 'harkPremium',
    tenantLogoUrl: 'https://gyms.fra1.digitaloceanspaces.com/hark/logo.png',
    seo: {
      title: 'Hark Movement Studio - Premium Pilates & Fitness',
      description: 'Premium pilates, CrossFit, and personal training classes across two locations. Elevate your practice at Hark Movement Studio.',
    },
    hero: {
      title: 'Elevate Your Practice',
      subtitle: 'Premium Pilates & More',
      description:
        'Discover a refined approach to fitness. From reformer pilates to CrossFit and personal training — find your strength at Hark Movement Studio.',
    },
    features: [
      {
        title: 'Reformer Pilates',
        description:
          'Sculpt and strengthen with our premium reformer pilates classes, guided by certified instructors in intimate group settings.',
      },
      {
        title: 'Mat Pilates',
        description:
          'Build core strength, flexibility, and body awareness through expertly led mat pilates sessions for all levels.',
      },
      {
        title: 'CrossFit',
        description:
          'High-intensity functional training that builds endurance, power, and resilience in a motivating group environment.',
      },
      {
        title: 'Personal Training',
        description:
          'One-on-one sessions tailored to your goals with dedicated coaches who push you to achieve your personal best.',
      },
      {
        title: 'Group Classes',
        description:
          'Dynamic group sessions that combine energy, community, and expert coaching for a complete workout experience.',
      },
      {
        title: 'Flexible Scheduling',
        description:
          'Book classes that fit your lifestyle with a wide range of time slots across both of our studio locations.',
      },
    ],
    benefits: [
      {
        title: 'Premium Studios',
        description:
          'Train in beautifully designed spaces with top-tier equipment, curated for focus and performance.',
      },
      {
        title: 'Expert Instructors',
        description:
          'Every class is led by certified professionals passionate about helping you grow.',
      },
      {
        title: 'Small Class Sizes',
        description:
          'Personalized attention in every session ensures proper form and maximum results.',
      },
      {
        title: 'Two Locations',
        description:
          'Choose between LakeView and Ambasador 3 — two studios built for your convenience.',
      },
      {
        title: 'All Levels Welcome',
        description:
          'Whether you are just starting out or an experienced athlete, our classes adapt to you.',
      },
      {
        title: 'Community Driven',
        description:
          'Join a supportive community that shares your commitment to health and well-being.',
      },
    ],
    cta: {
      primary: 'Register',
      secondary: 'Member Login',
    },
    locations: [
      {
        name: 'Hark LakeView',
        city: 'Tirana',
        focusAl: 'Pilates premium dhe klasa grupore',
        focusEn: 'Premium pilates and group classes',
        instagramUrl: 'https://www.instagram.com/hark.pilates/',
        instagramHandle: '@hark.pilates',
      },
      {
        name: 'Hark Ambasador 3',
        city: 'Tirana',
        focusAl: 'CrossFit, personal training dhe pilates',
        focusEn: 'CrossFit, personal training, and pilates',
        instagramUrl: 'https://www.instagram.com/hark.pilates/',
        instagramHandle: '@hark.pilates',
      },
    ],
    socialLinks: [
      {
        name: 'Hark Pilates',
        url: 'https://www.instagram.com/hark.pilates/',
        handle: '@hark.pilates',
        descriptionAl: 'Ndiq klasat, oraret dhe komunitetin tone ne Instagram.',
        descriptionEn: 'Follow our classes, schedules, and community on Instagram.',
      },
    ],
  },
};

const TENANT_ALIASES: Record<string, keyof typeof TENANT_CONFIGS> = {
  flexgym: 'flex',
  'flex-gym': 'flex',
  flexclasses: 'flex',
  'flex-classes': 'flex',
  flexsportsclub: 'flex',
  'flexsportsclub.al': 'flex',
  flexgym_tirane: 'flex',
  flexgymgreencoast: 'flex',
  'flex-green-coast': 'flex',
  greencoast: 'flex',
  hero: 'hark',
  'hark-pilates': 'hark',
  'hark.pilates': 'hark',
};

// Generic fallback configuration
export const GENERIC_CONFIG: TenantLandingConfig = {
  subdomain: 'generic',
  seo: {
    title: '{{businessName}} - Fitness Management',
    description: 'Join {{businessName}} and take the first step towards achieving your health and wellness goals.',
  },
  hero: {
    title: 'Welcome to {{businessName}}',
    subtitle: 'Your fitness journey starts here',
    description:
      'Join our community of fitness enthusiasts and take the first step towards achieving your health and wellness goals.',
  },
  features: [
    {
      title: 'Fitness Classes',
      description:
        'Join our diverse range of fitness classes, from yoga and pilates to high-intensity training and spinning.',
    },
    {
      title: 'Personal Training',
      description:
        'Work with certified trainers who will create personalized workout plans tailored to your goals.',
    },
    {
      title: 'Custom Nutrition Plans',
      description:
        'Get personalized diet plans designed to complement your fitness routine and help you achieve results.',
    },
    {
      title: 'State-of-the-Art Equipment',
      description:
        'Access the latest fitness equipment and facilities designed for optimal performance and comfort.',
    },
    {
      title: 'Flexible Membership',
      description:
        'Choose from a variety of membership options designed to match your schedule and budget.',
    },
    {
      title: 'Community Support',
      description:
        'Become part of a supportive community that motivates and inspires you to reach new heights.',
    },
  ],
  benefits: [
    {
      title: 'Expert Trainers',
      description:
        'Our certified professionals are dedicated to helping you succeed and stay motivated.',
    },
    {
      title: 'Convenient Hours',
      description:
        'Work out on your schedule with extended hours and flexible class times.',
    },
    {
      title: 'Results-Driven',
      description:
        'Track your progress and see real results with our comprehensive fitness programs.',
    },
    {
      title: 'Clean & Safe',
      description:
        'We maintain the highest standards of cleanliness and safety for your peace of mind.',
    },
    {
      title: 'Modern Technology',
      description:
        'Easy online booking, QR check-ins, and access to your workout plans anytime, anywhere.',
    },
    {
      title: 'Welcoming Atmosphere',
      description:
        "Whether you're a beginner or advanced, you'll feel at home in our inclusive environment.",
    },
  ],
  cta: {
    primary: 'Become a Member',
    secondary: 'Already a Member?',
  },
};

/**
 * Get landing page configuration for a specific tenant
 * Falls back to generic config if tenant doesn't have custom config
 */
export function getTenantConfig(subdomain: string): TenantLandingConfig {
  const normalized = subdomain.trim().toLowerCase();

  if (!normalized) {
    return GENERIC_CONFIG;
  }

  const directMatch = TENANT_CONFIGS[normalized];
  if (directMatch) {
    return directMatch;
  }

  const aliasMatch = TENANT_ALIASES[normalized];
  if (aliasMatch) {
    return TENANT_CONFIGS[aliasMatch];
  }

  if (normalized.includes('flex')) {
    return TENANT_CONFIGS.flex;
  }

  if (normalized.includes('repeat')) {
    return TENANT_CONFIGS.repeat;
  }

  if (normalized.includes('hark') || normalized.includes('hero')) {
    return TENANT_CONFIGS.hark;
  }

  return GENERIC_CONFIG;
}
